import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { MessageCircleQuestion, Send, Loader2, Crown, Clock, CheckCircle2, MinusCircle, MoreVertical, Flag, Ban, Sparkles, Lightbulb } from "lucide-react";

const Q_MIN = 5, Q_MAX = 300, A_MIN = 1, A_MAX = 500;
const statusMeta = {
  awaiting: { label: "Awaiting answer", cls: "bg-amber-100 text-amber-800", Icon: Clock },
  answered: { label: "Answered", cls: "bg-emerald-100 text-emerald-800", Icon: CheckCircle2 },
  skipped: { label: "Skipped", cls: "bg-slate-200 text-slate-600", Icon: MinusCircle },
};
const fmt = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

function AnswerBox({ q, otherName, onAnswer, onSkip }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (fn) => { setBusy(true); await fn(); setBusy(false); };
  return (
    <div className="mt-3 rounded-xl bg-mint/40 p-3" data-testid={`qa-answerbox-${q.id}`}>
      <p className="text-xs font-semibold text-emerald-700">Your answer (optional)</p>
      <Textarea value={text} maxLength={A_MAX} onChange={(e) => setText(e.target.value)} placeholder="Answer respectfully, or skip — it's always your choice." className="mt-1.5 bg-white text-sm" data-testid={`qa-answer-input-${q.id}`} />
      <div className="mt-2 flex items-center justify-between">
        <span className="text-xs text-slate-400">{text.length}/{A_MAX}</span>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => run(() => onSkip(q))} data-testid={`qa-skip-${q.id}`} className="text-slate-500">Skip</Button>
          <Button size="sm" disabled={busy || text.trim().length < A_MIN} onClick={() => run(async () => { const ok = await onAnswer(q, text); if (ok) setText(""); })} data-testid={`qa-answer-submit-${q.id}`} className="rounded-full bg-emerald-800 text-white hover:bg-emerald-900">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send answer"}</Button>
        </div>
      </div>
    </div>
  );
}

export default function QADialog({ open, onOpenChange, otherId, otherName = "your match" }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [draft, setDraft] = useState("");
  const [asking, setAsking] = useState(false);
  const [showSuggest, setShowSuggest] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/qa/thread", { params: { otherId } }); setData(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setData({ items: [], canAsk: false }); }
  }, [otherId]);

  useEffect(() => { if (open) { setData(null); setDraft(""); load(); } }, [open, load]);
  useEffect(() => { if (open && suggestions.length === 0) api.get("/qa/suggestions").then(({ data }) => setSuggestions(data.suggestions || [])).catch(() => {}); }, [open, suggestions.length]);

  const ask = async () => {
    const t = draft.trim();
    if (t.length < Q_MIN) return toast.error(`Questions must be at least ${Q_MIN} characters.`);
    setAsking(true);
    try { await api.post("/qa/ask", { targetId: otherId, text: t }); setDraft(""); setShowSuggest(false); toast.success("Question sent"); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } // moderation: draft kept
    finally { setAsking(false); }
  };
  const answer = async (q, text) => {
    try { await api.post("/qa/answer", { questionId: q.id, text: text.trim() }); toast.success("Answer sent"); await load(); return true; }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); return false; }
  };
  const skip = async (q) => { try { await api.post("/qa/skip", { questionId: q.id }); await load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const report = async (q) => { try { await api.post("/qa/report", { questionId: q.id, reason: "inappropriate" }); toast.success("Thank you. Our team will review this."); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const block = async () => { if (!window.confirm(`Block ${otherName}? You won't exchange further questions.`)) return; try { await api.post("/qa/block", { targetId: otherId }); toast.success("Blocked"); onOpenChange(false); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  const items = data?.items || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[88vh] max-w-lg flex-col overflow-hidden p-0" data-testid="qa-dialog">
        <DialogHeader className="border-b border-emerald-900/10 px-5 py-4">
          <DialogTitle className="flex items-center gap-2 font-serif text-xl text-emerald-900">
            <MessageCircleQuestion className="h-5 w-5 text-amber-600" /> Questions &amp; Answers
          </DialogTitle>
          <p className="text-sm font-normal text-slate-500">Private between you and {otherName}. Never shown on profiles.</p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {data === null ? (
            <div className="space-y-3">{[...Array(2)].map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>
          ) : items.length === 0 ? (
            <div className="py-6 text-center text-slate-500" data-testid="qa-empty">
              <Sparkles className="mx-auto mb-2 h-7 w-7 text-amber-400" />
              No questions yet. Start a thoughtful conversation about what matters for marriage.
            </div>
          ) : (
            <div className="space-y-3" data-testid="qa-thread">
              {items.map((q) => {
                const meta = statusMeta[q.status] || statusMeta.awaiting;
                const answerable = !q.mine && q.status === "awaiting";
                return (
                  <div key={q.id} className="rounded-2xl border border-emerald-900/10 bg-white p-4" data-testid={`qa-item-${q.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold text-emerald-700">{q.mine ? "You asked" : `${otherName} asked`} · {fmt(q.createdAt)}</span>
                      <div className="flex items-center gap-1.5">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${meta.cls}`}><meta.Icon className="h-3 w-3" /> {meta.label}</span>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><button data-testid={`qa-menu-${q.id}`} className="rounded-full p-1 text-slate-400 hover:bg-slate-100"><MoreVertical className="h-3.5 w-3.5" /></button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => report(q)} data-testid={`qa-report-${q.id}`}><Flag className="mr-2 h-4 w-4" /> Report</DropdownMenuItem>
                            <DropdownMenuItem onClick={block} data-testid="qa-block" className="text-red-600"><Ban className="mr-2 h-4 w-4" /> Block {otherName}</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                    <p className="mt-1.5 font-medium text-emerald-950">{q.question}</p>
                    {q.answer && <div className="mt-2 rounded-xl bg-mint/40 p-3"><p className="text-xs font-semibold text-emerald-700">{q.mine ? `${otherName} answered` : "You answered"}</p><p className="mt-0.5 text-sm text-slate-700">{q.answer}</p></div>}
                    {answerable && <AnswerBox q={q} otherName={otherName} onAnswer={answer} onSkip={skip} />}
                    {q.expiresAt && q.status === "awaiting" && <p className="mt-2 text-[11px] text-slate-400">Expires {fmt(q.expiresAt)}</p>}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Ask composer */}
        {data && (
          data.canAsk ? (
            <div className="border-t border-emerald-900/10 bg-ivory px-5 py-4" data-testid="qa-composer">
              {showSuggest && suggestions.length > 0 && (
                <div className="mb-2 max-h-28 overflow-y-auto rounded-xl bg-white p-2 ring-1 ring-emerald-900/10" data-testid="qa-suggestions">
                  {suggestions.map((s, i) => (
                    <button key={i} onClick={() => { setDraft(s.text); setShowSuggest(false); }} data-testid={`qa-suggestion-${i}`} className="block w-full rounded-lg px-2 py-1.5 text-left text-sm text-emerald-900 hover:bg-mint">
                      <span className="text-[10px] font-bold uppercase tracking-wide text-amber-600">{s.topic}</span><br />{s.text}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex items-end gap-2">
                <button onClick={() => setShowSuggest((v) => !v)} data-testid="qa-suggest-toggle" title="Suggested questions" className="mb-1 rounded-full p-2 text-amber-600 hover:bg-amber-50"><Lightbulb className="h-5 w-5" /></button>
                <Textarea value={draft} maxLength={Q_MAX} onChange={(e) => setDraft(e.target.value)} placeholder="Ask a respectful marriage question…" className="min-h-[44px] flex-1 bg-white text-sm" data-testid="qa-ask-input" />
                <Button onClick={ask} disabled={asking || draft.trim().length < Q_MIN} data-testid="qa-ask-submit" className="mb-0.5 rounded-full bg-emerald-800 text-white hover:bg-emerald-900">{asking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</Button>
              </div>
            </div>
          ) : (
            <div className="border-t border-emerald-900/10 bg-amber-50 px-5 py-4 text-center" data-testid="qa-upgrade-gate">
              <Crown className="mx-auto mb-1 h-6 w-6 text-amber-500" />
              <p className="text-sm font-semibold text-amber-900">Asking questions is a Premium feature</p>
              <p className="text-xs text-amber-800/90">You can always answer questions for free. Upgrade to start a conversation.</p>
              <Button onClick={() => { onOpenChange(false); navigate("/premium"); }} data-testid="qa-upgrade-btn" className="mt-3 rounded-full bg-emerald-800 text-white hover:bg-emerald-900">Upgrade to Premium</Button>
            </div>
          )
        )}
      </DialogContent>
    </Dialog>
  );
}
